import React, { useMemo } from 'react'
import PropTypes from 'prop-types'

import { ListItem, ListItemText, Box, Typography } from '@mui/material'

import { format, isValid } from 'date-fns'
import { convertDateTimeToPreferedTimezone } from 'data/dateTime/convertDateTimeToPreferedTimezone'
import { formatDateToFEFormat } from 'utils/date'

import T from 'T'
import { CHANGE_SOURCE } from './utils'

const ACTION_STYLES = {
  added: { fontWeight: 700, color: 'success.main' },
  removed: { fontWeight: 700, color: 'error.main' },
  default: { fontWeight: 700, color: 'info.light' },
}

const CHANGE_SOURCE_LABEL = {
  [CHANGE_SOURCE.DIRECT]: T.PRICE_CHANGE_SOURCE_CONFIGURED_SERVICE,
  [CHANGE_SOURCE.RENTAL_FEE_UPDATE]: T.PRICE_CHANGE_SOURCE_PRICE_BOOK,
}

const getDateTimeLabel = createdAt => {
  const { fullDateTime } = convertDateTimeToPreferedTimezone({ dateTime: createdAt })
  const parsed = fullDateTime ? new Date(fullDateTime) : null
  return parsed && isValid(parsed) ? format(parsed, 'MMMM d, yyyy h:mm a') : ''
}

const getPeriodActionParts = actionDescription => {
  const text = actionDescription ?? 'updated'
  let verb = 'updated'
  if (text.startsWith('added')) verb = 'added'
  else if (text.startsWith('deleted')) verb = 'removed'

  const prefixes = ['added ', 'updated ', 'deleted ']
  const match = prefixes.find(p => text.startsWith(p) && text !== p.trim())
  const rest = match ? ` ${text.slice(match.length)}` : ''

  return { verb, rest }
}

const PriceChangeContent = ({ user, actionDescription, changeSource, dateTimeLabel }) => {
  const actionText = actionDescription ?? 'updated price'
  const verb = actionText.startsWith('set') ? 'set' : 'changed'
  const rest = actionText.startsWith(verb) ? actionText.slice(verb.length) : ` ${actionText}`
  const sourceLabel = CHANGE_SOURCE_LABEL[changeSource]

  return (
    <>
      {user}{' '}
      <Box component="span" sx={ACTION_STYLES.default}>
        {verb}
      </Box>
      {rest}
      {sourceLabel && (
        <Box component="span" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
          {' '}
          ({sourceLabel})
        </Box>
      )}{' '}
      on {dateTimeLabel}
    </>
  )
}

PriceChangeContent.propTypes = {
  user: PropTypes.string.isRequired,
  actionDescription: PropTypes.string,
  changeSource: PropTypes.string,
  dateTimeLabel: PropTypes.string.isRequired,
}

const HistoryListItem = ({ history = {}, showDivider, ...rest }) => {
  const { type = 'grace_reset', user = '--', createdAt, resetDate, actionDescription, changeSource } = history
  const dateTimeLabel = useMemo(() => getDateTimeLabel(createdAt), [createdAt])
  const isPeriodActivity = type === 'period'
  const isPriceChange = type === 'price_change'

  const { verb: periodActionVerb, rest: periodActionRest } = useMemo(
    () => (isPeriodActivity ? getPeriodActionParts(actionDescription) : { verb: '', rest: '' }),
    [isPeriodActivity, actionDescription]
  )

  const renderHistoryContent = () => {
    if (isPriceChange) {
      return (
        <PriceChangeContent user={user} actionDescription={actionDescription} changeSource={changeSource} dateTimeLabel={dateTimeLabel} />
      )
    }

    if (isPeriodActivity) {
      return (
        <>
          {user}{' '}
          <Box component="span" sx={ACTION_STYLES[periodActionVerb] || ACTION_STYLES.default}>
            {periodActionVerb}
          </Box>
          {periodActionRest} on {dateTimeLabel}
        </>
      )
    }

    return (
      <>
        {user}{' '}
        <Box component="span" sx={ACTION_STYLES.default}>
          reset
        </Box>{' '}
        grace period at {formatDateToFEFormat(resetDate)} on {dateTimeLabel}
      </>
    )
  }

  const primary = (
    <Typography component="span" variant="h6" fontWeight={400} color="textPrimary">
      {renderHistoryContent()}
    </Typography>
  )

  return (
    <ListItem divider={showDivider} {...rest}>
      <ListItemText
        primary={primary}
        slotProps={{
          secondary: { variant: 'body1', fontWeight: 400, color: 'textPrimary' },
        }}
      />
    </ListItem>
  )
}

HistoryListItem.propTypes = {
  history: PropTypes.object,
  showDivider: PropTypes.bool,
}

export default HistoryListItem
